//import { MsalAuthProvider, LoginType } from 'react-aad-msal'
const tenantID: string = process.env.TENANT_ID as string
const applicationID: string = process.env.APP_ID as string
const redirectUri: string = `${window.location.origin}/tenants`
const signInAuthority = `https://login.microsoftonline.com/${tenantID}`
import { msalInstance } from '../../'
import {
  AuthenticationResult,
  Configuration,
  LogLevel,
} from '@azure/msal-browser'

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
  auth: {
    clientId: applicationID,
    authority: signInAuthority,
    redirectUri: redirectUri,
    postLogoutRedirectUri: '/',
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      /**
       * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
       */
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
      },
    },
  },
}

export const getUserEmail = async () => {
  const account = msalInstance.getActiveAccount()
  return account?.username
}

export const getUsername = async () => {
  // Get username from the msInstance
  const account = msalInstance.getActiveAccount()
  return account?.name
}

// Refresh the ID token this long before it expires, so a request never goes
// out with one that lapses in flight.
const ID_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000

const idTokenExpiresSoon = (response: AuthenticationResult) => {
  const exp = (response.idTokenClaims as { exp?: number } | undefined)?.exp
  return !exp || exp * 1000 - Date.now() < ID_TOKEN_REFRESH_MARGIN_MS
}

// Calls that arrive while a token is being fetched share that fetch.
let inFlight: Promise<AuthenticationResult | null> | null = null

/**
 * The token every API call sends (the API validates the ID token).
 *
 * It used to pass `forceRefresh: true`, which skips MSAL's cache: every API
 * call first made its own round trip to login.microsoftonline.com, and a page
 * firing five requests made five. Now the cached token is used until its ID
 * token is within five minutes of expiry, and only then refreshed.
 */
export const getToken = () => {
  if (!inFlight) {
    inFlight = acquireToken().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

const acquireToken = async (): Promise<AuthenticationResult | null> => {
  const tokenRequest = {
    scopes: ['User.Read'], // Add the scopes your app needs
    account: msalInstance?.getActiveAccount() ?? undefined, // Use the logged-in account
  }

  try {
    const cached = await msalInstance.acquireTokenSilent(tokenRequest)
    if (!idTokenExpiresSoon(cached)) {
      return cached
    }
    return await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      forceRefresh: true,
    })
  } catch (error) {
    console.error('Silent token acquisition failed:', error)
    msalInstance.acquireTokenRedirect(tokenRequest)
  }
  return null
}
